import { isEmpty } from 'lodash';
import React, { createContext, ReactNode, useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Group } from '../data/groups/Group';
import { Organization } from '../data/Organization';
import { CurrentUser } from '../data/users/CurrentUser';
import { useAnalyticsIdentity } from '../hooks/useAnalyticsIdentity';
import { useGroups } from '../hooks/api/groups/useGroups';
import { useCurrentUser } from '../hooks/api/useCurrentUser';
import { useOrganization } from '../hooks/api/useOrganization';
import { CurrentUserApiResponse, OrganizationApiResponse } from '../types/api.types';
import {
  getOnboardingSkipStatus,
  needsOnboarding,
  OnboardingSkipStatus,
} from '../utilities/onboardingStatus';

/**
 * Helper to create a CurrentUser instance from API response
 */
function createCurrentUserFromApi(data: CurrentUserApiResponse): CurrentUser {
  // Create a minimal CurrentUser instance and populate it with API data
  const user = Object.create(CurrentUser.prototype) as CurrentUser;
  user.initCurrentUserData(data);
  return user;
}

/**
 * Helper to create an Organization instance from API response
 */
function createOrganizationFromApi(data: OrganizationApiResponse): Organization {
  const org = Object.create(Organization.prototype) as Organization;
  org.onDataReceived(data);
  return org;
}

/**
 * App-level data context for migrating from class-based Data classes to React Query hooks.
 * This provider can be used by both class components (via context consumer) and function components (via hook).
 */

interface AppDataContextValue {
  // User
  user: CurrentUser | undefined;
  userLoading: boolean;
  userError: Error | null;
  refetchUser: () => void;

  // Organization
  organization: Organization | undefined;
  organizationLoading: boolean;
  organizationError: Error | null;
  refetchOrganization: () => void;

  // Groups
  groups: Group[] | undefined;
  groupsLoading: boolean;
  groupsError: Error | null;
  refetchGroups: () => void;

  // Onboarding
  onboardingSkipStatus: OnboardingSkipStatus;
}

const AppDataContext = createContext<AppDataContextValue | null>(null);

interface AppDataProviderProps {
  children: ReactNode;
}

/**
 * Provider component that fetches app-level data using React Query hooks.
 * Wrap your App component with this provider to access data via context.
 *
 * @example
 * // In index.tsx
 * <QueryClientProvider client={queryClient}>
 *   <AppDataProvider>
 *     <App />
 *   </AppDataProvider>
 * </QueryClientProvider>
 *
 * // In a class component
 * static contextType = AppDataContext;
 * // OR using consumer
 * <AppDataContext.Consumer>
 *   {({ groups, groupsLoading }) => <div>...</div>}
 * </AppDataContext.Consumer>
 */
export function AppDataProvider({ children }: AppDataProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // User query - always enabled
  const userQuery = useCurrentUser();

  // Organization query - enabled only when user is loaded
  const orgQuery = useOrganization({
    enabled: !!userQuery.data,
  });

  // Groups query - enabled only when user and org are loaded
  const groupsQuery = useGroups({
    enabled: !!userQuery.data && !!orgQuery.data,
  });

  // Transform API response to CurrentUser instance
  const user = useMemo(() => {
    if (!userQuery.data) return undefined;
    return createCurrentUserFromApi(userQuery.data);
  }, [userQuery.data]);

  // Transform API response to Organization instance
  const organization = useMemo(() => {
    if (!orgQuery.data) return undefined;
    return createOrganizationFromApi(orgQuery.data);
  }, [orgQuery.data]);

  // Transform API response to Group instances for backwards compatibility
  const groups = useMemo(() => {
    if (!groupsQuery.data?.groups) return undefined;
    return groupsQuery.data.groups.map((g) => new Group(g));
  }, [groupsQuery.data]);

  // Compute onboarding skip status
  const onboardingSkipStatus = useMemo(() => getOnboardingSkipStatus(user), [user]);

  // Foundational PostHog identify/group/platform:entered once user + org are loaded.
  useAnalyticsIdentity(user, organization);

  const value = useMemo<AppDataContextValue>(
    () => ({
      // User
      user,
      userLoading: userQuery.isLoading,
      userError: userQuery.error,
      refetchUser: userQuery.refetch,

      // Organization
      organization,
      organizationLoading: orgQuery.isLoading,
      organizationError: orgQuery.error,
      refetchOrganization: orgQuery.refetch,

      // Groups
      groups,
      groupsLoading: groupsQuery.isLoading,
      groupsError: groupsQuery.error,
      refetchGroups: groupsQuery.refetch,

      // Onboarding
      onboardingSkipStatus,
    }),
    [
      user,
      userQuery.isLoading,
      userQuery.error,
      userQuery.refetch,
      organization,
      orgQuery.isLoading,
      orgQuery.error,
      orgQuery.refetch,
      groups,
      groupsQuery.isLoading,
      groupsQuery.error,
      groupsQuery.refetch,
      onboardingSkipStatus,
    ]
  );

  useEffect(() => {
    if (isEmpty(user)) return;

    // Onboarding complete (or an impersonating admin, who /onboarding would only bounce back out)
    if (!needsOnboarding(user)) return;

    // Note: we deliberately do NOT bail out when every step can be skipped. Onboarding is where
    // CompleteOnboarding gets POSTed, and that is what stamps OnboardingCompletedDate, pushes the
    // HubSpot contact and runs the LearnUpon onboarding sync. A user whose fields were all supplied
    // by LU group sync would otherwise never be onboarded at all. /onboarding renders no steps for
    // them, completes immediately and redirects on.

    // Already on onboarding, don't redirect again
    if (location.pathname === '/onboarding') return;

    // Redirect to onboarding, preserving the original URL to return to after completion
    navigate('/onboarding', {
      replace: true,
      state: location.pathname !== '/' && { returnUrl: location.pathname + location.search },
    });
  }, [user, navigate, location]);

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

/**
 * Hook to access app data context (for function components)
 */
export function useAppData(): AppDataContextValue {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
}

/**
 * Export context for class components to use via static contextType
 */
export { AppDataContext };

/**
 * HOC to inject app data context into class components
 */
export interface WithAppDataProps {
  appData: AppDataContextValue;
}

export function withAppData<P extends WithAppDataProps>(
  WrappedComponent: React.ComponentType<P>
): React.FC<Omit<P, keyof WithAppDataProps>> {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithAppData: React.FC<Omit<P, keyof WithAppDataProps>> = (props) => {
    const appData = useAppData();
    return <WrappedComponent {...(props as P)} appData={appData} />;
  };

  ComponentWithAppData.displayName = `withAppData(${displayName})`;
  return ComponentWithAppData;
}
